import { __assign, __read, __spreadArray } from "tslib";
import { GraphQLObjectType, GraphQLInterfaceType, GraphQLUnionType, GraphQLEnumType, isScalarType, isObjectType, isInterfaceType, isUnionType, isEnumType, isInputObjectType, GraphQLInputObjectType, GraphQLScalarType, } from 'graphql';
import { mergeType, mergeInputType, mergeInterface, mergeUnion, mergeEnum, mergeScalar } from '@graphql-tools/merge';
import { validateFieldConsistency, validateInputFieldConsistency, validateInputObjectConsistency, } from './mergeValidations';
import { fieldToFieldConfig, inputFieldToFieldConfig } from '@graphql-tools/utils';
import { isSubschemaConfig } from '@graphql-tools/delegate';
export function mergeCandidates(typeName, candidates, typeMergingOptions) {
    var initialCandidateType = candidates[0].type;
    if (candidates.some(function (candidate) { return candidate.type.constructor !== initialCandidateType.constructor; })) {
        throw new Error("Cannot merge different type categories into common type " + typeName + ".");
    }
    if (isObjectType(initialCandidateType)) {
        return mergeObjectTypeCandidates(typeName, candidates, typeMergingOptions);
    }
    else if (isInputObjectType(initialCandidateType)) {
        return mergeInputObjectTypeCandidates(typeName, candidates, typeMergingOptions);
    }
    else if (isInterfaceType(initialCandidateType)) {
        return mergeInterfaceTypeCandidates(typeName, candidates, typeMergingOptions);
    }
    else if (isUnionType(initialCandidateType)) {
        return mergeUnionTypeCandidates(typeName, candidates, typeMergingOptions);
    }
    else if (isEnumType(initialCandidateType)) {
        return mergeEnumTypeCandidates(typeName, candidates, typeMergingOptions);
    }
    else if (isScalarType(initialCandidateType)) {
        return mergeScalarTypeCandidates(typeName, candidates, typeMergingOptions);
    }
    else {
        // not reachable.
        throw new Error("Type " + typeName + " has unknown GraphQL type.");
    }
}
function mergeObjectTypeCandidates(typeName, candidates, typeMergingOptions) {
    candidates = orderedTypeCandidates(candidates, typeMergingOptions);
    var description = mergeTypeDescriptions(candidates, typeMergingOptions);
    var fields = fieldConfigMapFromTypeCandidates(candidates, typeMergingOptions);
    var typeConfigs = candidates.map(function (candidate) { return candidate.type.toConfig(); });
    var interfaceMap = typeConfigs
        .map(function (typeConfig) { return typeConfig.interfaces; })
        .reduce(function (acc, interfaces) {
        if (interfaces != null) {
            interfaces.forEach(function (iface) {
                acc[iface.name] = iface;
            });
        }
        return acc;
    }, Object.create(null));
    var interfaces = Object.keys(interfaceMap).map(function (interfaceName) { return interfaceMap[interfaceName]; });
    var astNodes = pluck('astNode', candidates);
    var fieldAstNodes = canonicalFieldNamesForType(candidates)
        .map(function (fieldName) { var _a; return (_a = fields[fieldName]) === null || _a === void 0 ? void 0 : _a.astNode; })
        .filter(function (n) { return n != null; });
    if (astNodes.length > 1 && fieldAstNodes.length) {
        astNodes.push(__assign(__assign({}, astNodes[astNodes.length - 1]), { fields: JSON.parse(JSON.stringify(fieldAstNodes)) }));
    }
    var astNode = astNodes
        .slice(1)
        .reduce(function (acc, astNode) {
        return mergeType(astNode, acc, { ignoreFieldConflicts: true });
    }, astNodes[0]);
    var extensionASTNodes = [].concat(pluck('extensionASTNodes', candidates));
    var extensions = Object.assign.apply(Object, __spreadArray([{}], __read(pluck('extensions', candidates))));
    var typeConfig = {
        name: typeName,
        description: description,
        fields: fields,
        interfaces: interfaces,
        astNode: astNode,
        extensionASTNodes: extensionASTNodes,
        extensions: extensions,
    };
    return new GraphQLObjectType(typeConfig);
}
function mergeInputObjectTypeCandidates(typeName, candidates, typeMergingOptions) {
    candidates = orderedTypeCandidates(candidates, typeMergingOptions);
    var description = mergeTypeDescriptions(candidates, typeMergingOptions);
    var fields = inputFieldConfigMapFromTypeCandidates(candidates, typeMergingOptions);
    var astNodes = pluck('astNode', candidates);
    var fieldAstNodes = canonicalFieldNamesForType(candidates)
        .map(function (fieldName) { var _a; return (_a = fields[fieldName]) === null || _a === void 0 ? void 0 : _a.astNode; })
        .filter(function (n) { return n != null; });
    if (astNodes.length > 1 && fieldAstNodes.length) {
        astNodes.push(__assign(__assign({}, astNodes[astNodes.length - 1]), { fields: JSON.parse(JSON.stringify(fieldAstNodes)) }));
    }
    var astNode = astNodes.slice(1).reduce(function (acc, astNode) {
        return mergeInputType(astNode, acc, {
            ignoreFieldConflicts: true,
        });
    }, astNodes[0]);
    var extensionASTNodes = [].concat(pluck('extensionASTNodes', candidates));
    var extensions = Object.assign.apply(Object, __spreadArray([{}], __read(pluck('extensions', candidates))));
    var typeConfig = {
        name: typeName,
        description: description,
        fields: fields,
        astNode: astNode,
        extensionASTNodes: extensionASTNodes,
        extensions: extensions,
    };
    return new GraphQLInputObjectType(typeConfig);
}
function pluck(typeProperty, candidates) {
    return candidates.map(function (candidate) { return candidate.type[typeProperty]; }).filter(function (value) { return value != null; });
}
function mergeInterfaceTypeCandidates(typeName, candidates, typeMergingOptions) {
    candidates = orderedTypeCandidates(candidates, typeMergingOptions);
    var description = mergeTypeDescriptions(candidates, typeMergingOptions);
    var fields = fieldConfigMapFromTypeCandidates(candidates, typeMergingOptions);
    var typeConfigs = candidates.map(function (candidate) { return candidate.type.toConfig(); });
    var interfaceMap = typeConfigs
        .map(function (typeConfig) { return typeConfig.interfaces; })
        .reduce(function (acc, interfaces) {
        if (interfaces != null) {
            interfaces.forEach(function (iface) {
                acc[iface.name] = iface;
            });
        }
        return acc;
    }, Object.create(null));
    var interfaces = Object.keys(interfaceMap).map(function (interfaceName) { return interfaceMap[interfaceName]; });
    var astNodes = pluck('astNode', candidates);
    var fieldAstNodes = canonicalFieldNamesForType(candidates)
        .map(function (fieldName) { var _a; return (_a = fields[fieldName]) === null || _a === void 0 ? void 0 : _a.astNode; })
        .filter(function (n) { return n != null; });
    if (astNodes.length > 1 && fieldAstNodes.length) {
        astNodes.push(__assign(__assign({}, astNodes[astNodes.length - 1]), { fields: JSON.parse(JSON.stringify(fieldAstNodes)) }));
    }
    var astNode = astNodes.slice(1).reduce(function (acc, astNode) {
        return mergeInterface(astNode, acc, {
            ignoreFieldConflicts: true,
        });
    }, astNodes[0]);
    var extensionASTNodes = [].concat(pluck('extensionASTNodes', candidates));
    var extensions = Object.assign.apply(Object, __spreadArray([{}], __read(pluck('extensions', candidates))));
    var typeConfig = {
        name: typeName,
        description: description,
        fields: fields,
        interfaces: interfaces,
        astNode: astNode,
        extensionASTNodes: extensionASTNodes,
        extensions: extensions,
    };
    return new GraphQLInterfaceType(typeConfig);
}
function mergeUnionTypeCandidates(typeName, candidates, typeMergingOptions) {
    candidates = orderedTypeCandidates(candidates, typeMergingOptions);
    var description = mergeTypeDescriptions(candidates, typeMergingOptions);
    var typeConfigs = candidates.map(function (candidate) { return candidate.type.toConfig(); });
    var typeMap = typeConfigs.reduce(function (acc, typeConfig) {
        typeConfig.types.forEach(function (type) {
            acc[type.name] = type;
        });
        return acc;
    }, Object.create(null));
    var types = Object.keys(typeMap).map(function (typeName) { return typeMap[typeName]; });
    var astNodes = pluck('astNode', candidates);
    var astNode = astNodes
        .slice(1)
        .reduce(function (acc, astNode) { return mergeUnion(astNode, acc); }, astNodes[0]);
    var extensionASTNodes = [].concat(pluck('extensionASTNodes', candidates));
    var extensions = Object.assign.apply(Object, __spreadArray([{}], __read(pluck('extensions', candidates))));
    var typeConfig = {
        name: typeName,
        description: description,
        types: types,
        astNode: astNode,
        extensionASTNodes: extensionASTNodes,
        extensions: extensions,
    };
    return new GraphQLUnionType(typeConfig);
}
function mergeEnumTypeCandidates(typeName, candidates, typeMergingOptions) {
    candidates = orderedTypeCandidates(candidates, typeMergingOptions);
    var description = mergeTypeDescriptions(candidates, typeMergingOptions);
    var values = enumValueConfigMapFromTypeCandidates(candidates, typeMergingOptions);
    var astNodes = pluck('astNode', candidates);
    var astNode = astNodes
        .slice(1)
        .reduce(function (acc, astNode) {
        return mergeEnum(astNode, acc, { consistentEnumMerge: true });
    }, astNodes[0]);
    var extensionASTNodes = [].concat(pluck('extensionASTNodes', candidates));
    var extensions = Object.assign.apply(Object, __spreadArray([{}], __read(pluck('extensions', candidates))));
    var typeConfig = {
        name: typeName,
        description: description,
        values: values,
        astNode: astNode,
        extensionASTNodes: extensionASTNodes,
        extensions: extensions,
    };
    return new GraphQLEnumType(typeConfig);
}
function enumValueConfigMapFromTypeCandidates(candidates, typeMergingOptions) {
    var enumValueConfigCandidatesMap = Object.create(null);
    candidates.forEach(function (candidate) {
        var valueMap = candidate.type.toConfig().values;
        Object.keys(valueMap).forEach(function (enumValue) {
            var enumValueConfigCandidate = {
                enumValueConfig: valueMap[enumValue],
                enumValue: enumValue,
                type: candidate.type,
                subschema: candidate.subschema,
                transformedSubschema: candidate.transformedSubschema,
            };
            if (enumValue in enumValueConfigCandidatesMap) {
                enumValueConfigCandidatesMap[enumValue].push(enumValueConfigCandidate);
            }
            else {
                enumValueConfigCandidatesMap[enumValue] = [enumValueConfigCandidate];
            }
        });
    });
    var enumValueConfigMap = Object.create(null);
    Object.keys(enumValueConfigCandidatesMap).forEach(function (enumValue) {
        var _a;
        var enumValueConfigMerger = (_a = typeMergingOptions === null || typeMergingOptions === void 0 ? void 0 : typeMergingOptions.enumValueConfigMerger) !== null && _a !== void 0 ? _a : defaultEnumValueConfigMerger;
        enumValueConfigMap[enumValue] = enumValueConfigMerger(enumValueConfigCandidatesMap[enumValue]);
    });
    return enumValueConfigMap;
}
function defaultEnumValueConfigMerger(candidates) {
    var preferred = candidates.find(function (_a) {
        var _b, _c;
        var type = _a.type, transformedSubschema = _a.transformedSubschema;
        return isSubschemaConfig(transformedSubschema) && ((_c = (_b = transformedSubschema.merge) === null || _b === void 0 ? void 0 : _b[type.name]) === null || _c === void 0 ? void 0 : _c.canonical);
    });
    return (preferred || candidates[candidates.length - 1]).enumValueConfig;
}
function mergeScalarTypeCandidates(typeName, candidates, typeMergingOptions) {
    candidates = orderedTypeCandidates(candidates, typeMergingOptions);
    var description = mergeTypeDescriptions(candidates, typeMergingOptions);
    var serializeFns = pluck('serialize', candidates);
    var serialize = serializeFns[serializeFns.length - 1];
    var parseValueFns = pluck('parseValue', candidates);
    var parseValue = parseValueFns[parseValueFns.length - 1];
    var parseLiteralFns = pluck('parseLiteral', candidates);
    var parseLiteral = parseLiteralFns[parseLiteralFns.length - 1];
    var astNodes = pluck('astNode', candidates);
    var astNode = astNodes
        .slice(1)
        .reduce(function (acc, astNode) { return mergeScalar(astNode, acc); }, astNodes[0]);
    var extensionASTNodes = [].concat(pluck('extensionASTNodes', candidates));
    var extensions = Object.assign.apply(Object, __spreadArray([{}], __read(pluck('extensions', candidates))));
    var typeConfig = {
        name: typeName,
        description: description,
        serialize: serialize,
        parseValue: parseValue,
        parseLiteral: parseLiteral,
        astNode: astNode,
        extensionASTNodes: extensionASTNodes,
        extensions: extensions,
    };
    return new GraphQLScalarType(typeConfig);
}
function orderedTypeCandidates(candidates, typeMergingOptions) {
    var _a;
    var typeCandidateMerger = (_a = typeMergingOptions === null || typeMergingOptions === void 0 ? void 0 : typeMergingOptions.typeCandidateMerger) !== null && _a !== void 0 ? _a : defaultTypeCandidateMerger;
    var candidate = typeCandidateMerger(candidates);
    return candidates.filter(function (c) { return c !== candidate; }).concat([candidate]);
}
function defaultTypeCandidateMerger(candidates) {
    var canonical = candidates.filter(function (_a) {
        var _b, _c;
        var type = _a.type, transformedSubschema = _a.transformedSubschema;
        return isSubschemaConfig(transformedSubschema) ? (_c = (_b = transformedSubschema.merge) === null || _b === void 0 ? void 0 : _b[type.name]) === null || _c === void 0 ? void 0 : _c.canonical : false;
    });
    if (canonical.length > 1) {
        throw new Error("Multiple canonical definitions for \"" + canonical[0].type.name + "\"");
    }
    else if (canonical.length) {
        return canonical[0];
    }
    return candidates[candidates.length - 1];
}
function mergeTypeDescriptions(candidates, typeMergingOptions) {
    var _a;
    var typeDescriptionsMerger = (_a = typeMergingOptions === null || typeMergingOptions === void 0 ? void 0 : typeMergingOptions.typeDescriptionsMerger) !== null && _a !== void 0 ? _a : defaultTypeDescriptionMerger;
    return typeDescriptionsMerger(candidates);
}
function defaultTypeDescriptionMerger(candidates) {
    return candidates[candidates.length - 1].type.description;
}
function fieldConfigMapFromTypeCandidates(candidates, typeMergingOptions) {
    var fieldConfigCandidatesMap = Object.create(null);
    candidates.forEach(function (candidate) {
        var fieldMap = candidate.type.getFields();
        Object.keys(fieldMap).forEach(function (fieldName) {
            var fieldConfigCandidate = {
                fieldConfig: fieldToFieldConfig(fieldMap[fieldName]),
                fieldName: fieldName,
                type: candidate.type,
                subschema: candidate.subschema,
                transformedSubschema: candidate.transformedSubschema,
            };
            if (fieldName in fieldConfigCandidatesMap) {
                fieldConfigCandidatesMap[fieldName].push(fieldConfigCandidate);
            }
            else {
                fieldConfigCandidatesMap[fieldName] = [fieldConfigCandidate];
            }
        });
    });
    var fieldConfigMap = Object.create(null);
    Object.keys(fieldConfigCandidatesMap).forEach(function (fieldName) {
        fieldConfigMap[fieldName] = mergeFieldConfigs(fieldConfigCandidatesMap[fieldName], typeMergingOptions);
    });
    return fieldConfigMap;
}
function mergeFieldConfigs(candidates, typeMergingOptions) {
    var _a;
    var fieldConfigMerger = (_a = typeMergingOptions === null || typeMergingOptions === void 0 ? void 0 : typeMergingOptions.fieldConfigMerger) !== null && _a !== void 0 ? _a : defaultFieldConfigMerger;
    var finalFieldConfig = fieldConfigMerger(candidates);
    validateFieldConsistency(finalFieldConfig, candidates, typeMergingOptions);
    return finalFieldConfig;
}
function defaultFieldConfigMerger(candidates) {
    var canonicalByField = [];
    var canonicalByType = [];
    candidates.forEach(function (_a) {
        var _b, _c, _d, _e, _f, _g;
        var type = _a.type, fieldName = _a.fieldName, fieldConfig = _a.fieldConfig, transformedSubschema = _a.transformedSubschema;
        if (!isSubschemaConfig(transformedSubschema))
            return;
        if ((_e = (_d = (_c = (_b = transformedSubschema.merge) === null || _b === void 0 ? void 0 : _b[type.name]) === null || _c === void 0 ? void 0 : _c.fields) === null || _d === void 0 ? void 0 : _d[fieldName]) === null || _e === void 0 ? void 0 : _e.canonical) {
            canonicalByField.push(fieldConfig);
        }
        else if ((_g = (_f = transformedSubschema.merge) === null || _f === void 0 ? void 0 : _f[type.name]) === null || _g === void 0 ? void 0 : _g.canonical) {
            canonicalByType.push(fieldConfig);
        }
    });
    if (canonicalByField.length > 1) {
        throw new Error("Multiple canonical definitions for \"" + candidates[0].type.name + "." + candidates[0].fieldName + "\"");
    }
    else if (canonicalByField.length) {
        return canonicalByField[0];
    }
    else if (canonicalByType.length) {
        return canonicalByType[0];
    }
    return candidates[candidates.length - 1].fieldConfig;
}
function inputFieldConfigMapFromTypeCandidates(candidates, typeMergingOptions) {
    var inputFieldConfigCandidatesMap = Object.create(null);
    var fieldInclusionMap = Object.create(null);
    candidates.forEach(function (candidate) {
        var inputFieldMap = candidate.type.getFields();
        Object.keys(inputFieldMap).forEach(function (fieldName) {
            fieldInclusionMap[fieldName] = fieldInclusionMap[fieldName] || 0;
            fieldInclusionMap[fieldName] += 1;
            var inputFieldConfigCandidate = {
                inputFieldConfig: inputFieldToFieldConfig(inputFieldMap[fieldName]),
                fieldName: fieldName,
                type: candidate.type,
                subschema: candidate.subschema,
                transformedSubschema: candidate.transformedSubschema,
            };
            if (fieldName in inputFieldConfigCandidatesMap) {
                inputFieldConfigCandidatesMap[fieldName].push(inputFieldConfigCandidate);
            }
            else {
                inputFieldConfigCandidatesMap[fieldName] = [inputFieldConfigCandidate];
            }
        });
    });
    validateInputObjectConsistency(fieldInclusionMap, candidates, typeMergingOptions);
    var inputFieldConfigMap = Object.create(null);
    Object.keys(inputFieldConfigCandidatesMap).forEach(function (fieldName) {
        var _a;
        var inputFieldConfigMerger = (_a = typeMergingOptions === null || typeMergingOptions === void 0 ? void 0 : typeMergingOptions.inputFieldConfigMerger) !== null && _a !== void 0 ? _a : defaultInputFieldConfigMerger;
        inputFieldConfigMap[fieldName] = inputFieldConfigMerger(inputFieldConfigCandidatesMap[fieldName]);
        validateInputFieldConsistency(inputFieldConfigMap[fieldName], inputFieldConfigCandidatesMap[fieldName], typeMergingOptions);
    });
    return inputFieldConfigMap;
}
function defaultInputFieldConfigMerger(candidates) {
    var canonicalByField = [];
    var canonicalByType = [];
    candidates.forEach(function (_a) {
        var _b, _c, _d, _e, _f, _g;
        var type = _a.type, fieldName = _a.fieldName, inputFieldConfig = _a.inputFieldConfig, transformedSubschema = _a.transformedSubschema;
        if (!isSubschemaConfig(transformedSubschema))
            return;
        if ((_e = (_d = (_c = (_b = transformedSubschema.merge) === null || _b === void 0 ? void 0 : _b[type.name]) === null || _c === void 0 ? void 0 : _c.fields) === null || _d === void 0 ? void 0 : _d[fieldName]) === null || _e === void 0 ? void 0 : _e.canonical) {
            canonicalByField.push(inputFieldConfig);
        }
        else if ((_g = (_f = transformedSubschema.merge) === null || _f === void 0 ? void 0 : _f[type.name]) === null || _g === void 0 ? void 0 : _g.canonical) {
            canonicalByType.push(inputFieldConfig);
        }
    });
    if (canonicalByField.length > 1) {
        throw new Error("Multiple canonical definitions for \"" + candidates[0].type.name + "." + candidates[0].fieldName + "\"");
    }
    else if (canonicalByField.length) {
        return canonicalByField[0];
    }
    else if (canonicalByType.length) {
        return canonicalByType[0];
    }
    return candidates[candidates.length - 1].inputFieldConfig;
}
function canonicalFieldNamesForType(candidates) {
    var canonicalFieldNames = Object.create(null);
    candidates.forEach(function (_a) {
        var _b, _c;
        var type = _a.type, transformedSubschema = _a.transformedSubschema;
        if (isSubschemaConfig(transformedSubschema) &&
            ((_c = (_b = transformedSubschema.merge) === null || _b === void 0 ? void 0 : _b[type.name]) === null || _c === void 0 ? void 0 : _c.fields) &&
            !transformedSubschema.merge[type.name].canonical) {
            Object.entries(transformedSubschema.merge[type.name].fields).forEach(function (_a) {
                var _b = __read(_a, 2), fieldName = _b[0], mergedFieldConfig = _b[1];
                if (mergedFieldConfig.canonical) {
                    canonicalFieldNames[fieldName] = true;
                }
            });
        }
    });
    return Object.keys(canonicalFieldNames);
}
//# sourceMappingURL=mergeCandidates.js.map